import { ChatIcon, ChevronDownIcon, RepeatClockIcon, TimeIcon, WarningTwoIcon } from '@chakra-ui/icons'
import {
  Button,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, formatPhone } from '../../../helpers'
import { RequestService } from '../../../services/Request/RequestService'
import { IRequestListItem } from '../../../services/Request/types'
import { statuses } from './RequestConsts'
import { RequestDeleteDialog } from './RequestDeleteDialog'
import { RequestViewModal } from './RequestViewModal'

import './Request.css'

/** `length` fica de fora: é o tamanho de página escolhido no Paginator, não um filtro. */
const INITIAL_FILTERS = {
  tipo_solicitacao: '',
  consultor_de_sucesso: '',
  formato_curso: '',
  status: 'active',
  created_at: { from: '', to: '' },
  solved_at: { from: '', to: '' },
  search: { value: '' }
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aberto/Em andamento' },
  { value: 'open', label: 'Aberto' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'waiting_payment', label: 'Aguardando Pagamento' },
  { value: 'waiting_for_a_place', label: 'Aguardando Vaga' },
  { value: 'in_management_analysis', label: 'Em análise da gestão' },
  { value: 'solved', label: 'Resolvido' }
]

const COURSE_FORMAT_OPTIONS = [
  { value: 'Presencial', label: 'Presencial' },
  { value: 'EAD Ao Vivo', label: 'EAD Ao Vivo' },
  { value: 'Online (videoaulas)', label: 'Online' }
]

interface ISortableThProps {
  column: number
  order: { column: number; dir: string }
  onOrder: (column: number) => void
  children: ReactNode
}

/**
 * Cabeçalho ordenável — a única tabela ordenável do painel.
 *
 * O `<Th>` continua sendo um `columnheader` e ganha o `aria-sort`; quem recebe o clique e o
 * foco é o `<button>` de dentro, que é o que torna a ordenação operável por teclado. Antes o
 * `onClick` ficava no próprio `<Th>`, fora da ordem de tabulação.
 */
const SortableTh = ({ column, order, onOrder, children }: ISortableThProps) => {
  const isActive = order.column === column

  return (
    <Th
      className={`ordering ${isActive ? 'active' : ''} ${order.dir}`}
      aria-sort={isActive ? (order.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="ordering-button" onClick={() => onOrder(column)}>
        {children}
      </button>
    </Th>
  )
}

const courseLabel = (request: IRequestListItem) => `Curso ${request.formato_curso.toLowerCase()} - ${request.course}`

/** Date over time, so the column is only as wide as the date. */
const StackedDateTime = ({ value }: { value: string }) => {
  const [day, time] = formatDateTime(value, { showSeconds: true }).split(' ')

  return (
    <>
      <Text>{day}</Text>
      <Text variant="caption">{time}</Text>
    </>
  )
}

export default function RequestsPage() {
  const requestService = new RequestService()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requests, setRequests] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalRequest, setModalRequest] = useState<any>({ roles: [] })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requestToDelete, setRequestToDelete] = useState<any>(null)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const [order, setOrder] = useState<{ column: number; dir: string }>({ column: 7, dir: 'desc' })
  const [requestTypes, setRequestTypes] = useState<string[]>([])
  const [requestConsultants, setRequestConsultants] = useState<string[]>([])
  const [openedRequests, setOpenedRequests] = useState<{
    open: number
    in_progress: number
    waiting_payment: number
    waiting_for_a_place: number
    in_management_analysis: number
    avg_resolution_time: number
  }>({
    open: 0,
    in_progress: 0,
    waiting_payment: 0,
    waiting_for_a_place: 0,
    in_management_analysis: 0,
    avg_resolution_time: 0
  })
  const [filters, setFilters] = useState({ length: DEFAULT_PAGE_SIZE, ...INITIAL_FILTERS })
  const isDirty =
    filters.tipo_solicitacao !== '' ||
    filters.consultor_de_sucesso !== '' ||
    filters.formato_curso !== '' ||
    filters.status !== 'active' ||
    filters.created_at.from !== '' ||
    filters.created_at.to !== '' ||
    filters.solved_at.from !== '' ||
    filters.solved_at.to !== '' ||
    filters.search.value !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const hasRole = useHasRole()
  const permissions = {
    editRequests: hasRole('edit_requests'),
    deleteRequests: hasRole('delete_requests')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    loadRequests(page)
  }

  const loadRequests = async (page: number) => {
    const { recordsFiltered, data } = await requestService.list({
      start: (page - 1) * filters.length,
      order: [order],
      ...filters
    })

    setRequests(data)

    setTotalPages(Math.ceil(recordsFiltered / filters.length))

    calcOpenedRequests()
  }

  useEffect(() => {
    loadRequests(page)
  }, [order, filters])

  useEffect(() => {
    Promise.all([requestService.getRequestsTypes(), requestService.getRequestsConsultants()]).then(
      ([types, consultants]) => {
        setRequestTypes(types.requestTypes)
        setRequestConsultants(consultants.consultants)
      }
    )
  }, [])

  const calcOpenedRequests = async () => {
    const openRequests = await requestService.getOpenRequests(filters)
    setOpenedRequests(openRequests.openedRequests)
  }

  const handleOpenModal = async (requestId: number) => {
    const request = await requestService.get(requestId)

    setModalRequest(request)
    setIsModalOpen(true)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDeleteDialog = (request: any) => {
    deleteDialogOnOpen()
    setRequestToDelete(request)
  }

  const refreshRequest = async (requestId: number) => {
    const request = await requestService.get(requestId)

    setModalRequest(request)
  }

  const handleOrder = (column: number) => {
    if (order.column == column) {
      setOrder({ column, dir: order.dir == 'asc' ? 'desc' : 'asc' })
    } else {
      setOrder({ column, dir: 'asc' })
    }
  }

  const handleStatusChange = async (requestId: number, status: string) => {
    await requestService.setNewStatus(requestId, status)
    loadRequests(page)
  }

  // A string is both the tooltip and the aria-label of the button.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSolvedTooltip = (request: any) =>
    `Concluído em: ${formatDateTime(request.solved_at)} · Tempo de resolução: ` +
    `${Math.floor(request.tempo_resolucao / 60)} horas e ${request.tempo_resolucao % 60} minutos`

  return (
    <>
      <PageTitle
        title="Gerenciamento de solicitações de alunos"
        description="Pedidos enviados pelos alunos e seu andamento"
        mb="2"
        actions={
          <div>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Solicitações em aberto: {openedRequests.open}
            </Text>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Solicitações em andamento: {openedRequests.in_progress}
            </Text>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Solicitações aguardando pagamento: {openedRequests.waiting_payment}
            </Text>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Solicitações aguardando vaga: {openedRequests.waiting_for_a_place}
            </Text>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Solicitações em análise da gestão: {openedRequests.in_management_analysis}
            </Text>
            <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
              Tempo médio de resolução: {Math.floor(openedRequests.avg_resolution_time / 60)} hrs{' '}
              {Math.floor(openedRequests.avg_resolution_time % 60)} min
            </Text>
          </div>
        }
      />

      <FilterBar onClear={() => updateFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterSearch
          value={filters.search.value}
          onChange={(value) => updateFilters({ search: { value } })}
          placeholder="Buscar solicitação"
        />
        <FilterSelect
          label="Tipo de solicitação"
          value={filters.tipo_solicitacao}
          onChange={(tipo_solicitacao) => updateFilters({ tipo_solicitacao })}
          options={requestTypes.map((type) => ({ value: type, label: type }))}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => updateFilters({ status })}
          options={STATUS_OPTIONS}
        />
        <FilterSelect
          label="Consultor/Setor"
          value={filters.consultor_de_sucesso}
          onChange={(consultor_de_sucesso) => updateFilters({ consultor_de_sucesso })}
          options={[
            { value: '-1', label: 'Não informado' },
            ...requestConsultants.map((consultant) => ({ value: consultant, label: consultant }))
          ]}
        />
        <FilterSelect
          label="Formato de ensino"
          value={filters.formato_curso}
          onChange={(formato_curso) => updateFilters({ formato_curso })}
          options={COURSE_FORMAT_OPTIONS}
        />
        <FilterDateRange
          label="Data da solicitação"
          value={{ start: filters.created_at.from, end: filters.created_at.to }}
          onChange={(date) => updateFilters({ created_at: { from: date.start, to: date.end } })}
        />
        <FilterDateRange
          label="Data de resolução"
          value={{ start: filters.solved_at.from, end: filters.solved_at.to }}
          onChange={(date) => updateFilters({ solved_at: { from: date.start, to: date.end } })}
        />
        <ActionButton action="refresh" label="Atualizar listagem" mr={0} onClick={() => loadRequests(page)} />
      </FilterBar>

      <TableContainer>
        {/* Headers and text cells wrap so the 10 columns fit from 1366px on; the container's
            nowrap would otherwise size each column by its longest value. */}
        <Table id="requests-table" variant="striped" sx={{ 'th, td': { px: 1.5 }, th: { whiteSpace: 'normal' } }}>
          <Thead>
            <Tr>
              <SortableTh column={0} order={order} onOrder={handleOrder}>
                #
              </SortableTh>
              <SortableTh column={1} order={order} onOrder={handleOrder}>
                Tipo de Solicitação
              </SortableTh>
              <SortableTh column={2} order={order} onOrder={handleOrder}>
                Consultor(a) / Setor
              </SortableTh>
              <SortableTh column={3} order={order} onOrder={handleOrder}>
                Aluno(a)
              </SortableTh>
              <SortableTh column={4} order={order} onOrder={handleOrder}>
                Responsável
              </SortableTh>
              <SortableTh column={5} order={order} onOrder={handleOrder}>
                Curso
              </SortableTh>
              <SortableTh column={6} order={order} onOrder={handleOrder}>
                Telefone
              </SortableTh>
              <SortableTh column={7} order={order} onOrder={handleOrder}>
                Data
              </SortableTh>
              <SortableTh column={8} order={order} onOrder={handleOrder}>
                Status
              </SortableTh>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((request) => (
              <Tr key={request.id}>
                <Td>{request.id}</Td>
                {/* minW sits on the inner element: min-width on a table cell is not reliable. */}
                <Td whiteSpace="normal">
                  {/* O balão fica no texto truncado, que é o que ele existe para revelar. Havia um
                      segundo <Tooltip> em volta do próprio <Td>, com o mesmo label. */}
                  <Tooltip label={request.tipoDeSolicitacao}>
                    <Text noOfLines={2} minW="110px">
                      {request.tipoDeSolicitacao}
                    </Text>
                  </Tooltip>
                </Td>
                <Td whiteSpace="normal">
                  <Text minW="90px">{request.consultor_de_sucesso}</Text>
                </Td>
                <Td whiteSpace="normal">
                  <Text minW="110px">{request.your_name}</Text>
                </Td>
                <Td whiteSpace="normal">
                  <EmptyCell value={request.nome_responsavel} display="block" minW="100px" />
                </Td>
                <Td whiteSpace="normal">
                  <Tooltip label={courseLabel(request)}>
                    <Text noOfLines={3} minW="120px">
                      {courseLabel(request)}
                    </Text>
                  </Tooltip>
                </Td>
                <Td>
                  <Link
                    href={`https://wa.me/55${request.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    color="blue.500"
                  >
                    {formatPhone(request.phone)}
                  </Link>
                </Td>
                <Td>
                  <StackedDateTime value={request.created_at} />
                </Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      size="xs"
                      colorScheme={statuses[request.status].color}
                      color={statuses[request.status].textColor}
                      disabled={!permissions.editRequests}
                    >
                      {statuses[request.status].text}
                    </MenuButton>
                    <MenuList>
                      <MenuItem icon={<RepeatClockIcon />} onClick={() => handleStatusChange(request.id, 'open')}>
                        Aberto
                      </MenuItem>
                      <MenuItem icon={<ChatIcon />} onClick={() => handleStatusChange(request.id, 'in_progress')}>
                        Em andamento
                      </MenuItem>
                      <MenuItem
                        icon={<TimeIcon />}
                        onClick={() => handleStatusChange(request.id, 'waiting_for_a_place')}
                      >
                        Aguardando Vaga
                      </MenuItem>
                      {request.tipoDeSolicitacao === 'Carteira de Estudante ABRA' && (
                        <MenuItem icon={<TimeIcon />} onClick={() => handleStatusChange(request.id, 'waiting_payment')}>
                          Aguardando pagamento
                        </MenuItem>
                      )}
                      <MenuItem
                        icon={<WarningTwoIcon />}
                        onClick={() => handleStatusChange(request.id, 'in_management_analysis')}
                      >
                        Em análise da gestão
                      </MenuItem>
                      <MenuItem
                        icon={<Icon name="lni lni-checkmark" />}
                        onClick={() => handleStatusChange(request.id, 'solved')}
                      >
                        Resolvido
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
                <Td>
                  <HStack spacing={1} justifyContent="center">
                    {request.status == 'solved' && (
                      <ActionButton
                        action="info"
                        icon={<Icon name="lni lni-hourglass" />}
                        label={handleSolvedTooltip(request)}
                        colorScheme="abraGreen"
                        variant="solid"
                        mr={0}
                      />
                    )}
                    <ActionButton action="view" mr={0} onClick={() => handleOpenModal(request.id)} />
                    {permissions.deleteRequests && (
                      <ActionButton action="delete" mr={0} onClick={() => handleDeleteDialog(request)} />
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadRequests(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      <RequestViewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        request={modalRequest}
        refreshRequest={refreshRequest}
      />

      <RequestDeleteDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        request={requestToDelete}
        onDelete={() => {
          deleteDialogIsClose()
          loadRequests(page)
        }}
      />
    </>
  )
}
